import {
  VIOLATION_SEVERITIES,
  VIOLATION_SEVERITY_LABELS,
  VIOLATION_SOURCE_DESCRIPTORS,
  VIOLATION_SOURCE_IDS,
  type ViolationSeverity,
} from '@sitewise/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ErrorState,
  Input,
  Label,
  Skeleton,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { fetchOrgSettings, patchOrgSettings } from '../../../api/org-settings.js';
import { formatPollInterval } from '../violations/lib.js';

/**
 * `/settings/violations` — admin / building-manager only. Mirrors the
 * shape of `WorkOrdersSettingsPage`. Lets admins toggle auto-WO
 * creation, set the severity threshold, override per-source intervals,
 * and configure quiet hours.
 */
export function ViolationsSettingsPage() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['org-settings'],
    queryFn: fetchOrgSettings,
  });
  const [autoCreate, setAutoCreate] = useState(false);
  const [threshold, setThreshold] = useState<ViolationSeverity>('high');
  const [intervals, setIntervals] = useState<Record<string, number>>({});
  const [quiet, setQuiet] = useState({
    enabled: true,
    startHour: 23,
    endHour: 6,
    timezone: 'America/New_York',
  });

  useEffect(() => {
    if (!settingsQuery.data) return;
    const v = settingsQuery.data.settings.violations;
    setAutoCreate(v.autoCreateWorkOrders);
    setThreshold(v.autoCreateWorkOrdersThreshold);
    setIntervals({ ...v.pollingIntervalOverrides });
    setQuiet({
      enabled: v.quietHours.enabled,
      startHour: v.quietHours.startHour,
      endHour: v.quietHours.endHour,
      timezone: v.quietHours.timezone,
    });
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      patchOrgSettings({
        violations: {
          autoCreateWorkOrders: autoCreate,
          autoCreateWorkOrdersThreshold: threshold,
          pollingIntervalOverrides: intervals as never,
          quietHours: quiet,
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-settings'] }),
  });

  if (settingsQuery.isError) {
    return <ErrorState description="Could not load settings." />;
  }
  if (settingsQuery.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Auto-create work orders</h2>
          <p className="text-xs text-fg-muted">
            Off by default. Violations stay as violations until you turn one into a work
            order from its detail page. Enable only if you want SiteWise to create work
            orders automatically when severity meets the threshold.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoCreate}
              onChange={(e) => setAutoCreate(e.target.checked)}
            />
            Enable auto-create
          </label>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Threshold</Label>
            <select
              value={threshold}
              onChange={(e) => setThreshold(e.target.value as ViolationSeverity)}
              className="h-9 rounded border border-border bg-surface px-2 text-sm"
              disabled={!autoCreate}
            >
              {VIOLATION_SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {VIOLATION_SEVERITY_LABELS[s]} and above
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Polling intervals</h2>
          <p className="text-xs text-fg-muted">
            Override how often SiteWise polls each source. Leave blank to use the default.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {VIOLATION_SOURCE_IDS.map((sourceId) => {
            const desc = VIOLATION_SOURCE_DESCRIPTORS[sourceId];
            const overrideMs = intervals[sourceId];
            return (
              <div key={sourceId} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">{desc.displayName}</div>
                  <div className="text-xs text-fg-muted">
                    Default: {formatPollInterval(desc.defaultPollIntervalMs)}
                  </div>
                </div>
                <Input
                  type="number"
                  min={1}
                  className="w-28"
                  placeholder="minutes"
                  value={overrideMs ? String(Math.round(overrideMs / 60_000)) : ''}
                  onChange={(e) => {
                    const minutes = Number.parseInt(e.target.value, 10);
                    setIntervals((prev) => {
                      const next = { ...prev };
                      if (Number.isFinite(minutes) && minutes > 0) {
                        next[sourceId] = minutes * 60_000;
                      } else {
                        delete next[sourceId];
                      }
                      return next;
                    });
                  }}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Quiet hours</h2>
          <p className="text-xs text-fg-muted">
            Skip polls + suppress non-critical notifications during this window.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={quiet.enabled}
              onChange={(e) => setQuiet({ ...quiet, enabled: e.target.checked })}
            />
            Enable quiet hours
          </label>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="qstart">Start hour</Label>
              <Input
                id="qstart"
                type="number"
                min={0}
                max={23}
                value={quiet.startHour}
                onChange={(e) =>
                  setQuiet({ ...quiet, startHour: Number.parseInt(e.target.value, 10) || 0 })
                }
                className="w-24"
              />
            </div>
            <div>
              <Label htmlFor="qend">End hour</Label>
              <Input
                id="qend"
                type="number"
                min={0}
                max={23}
                value={quiet.endHour}
                onChange={(e) =>
                  setQuiet({ ...quiet, endHour: Number.parseInt(e.target.value, 10) || 0 })
                }
                className="w-24"
              />
            </div>
            <div>
              <Label htmlFor="qtz">Timezone</Label>
              <Input
                id="qtz"
                value={quiet.timezone}
                onChange={(e) => setQuiet({ ...quiet, timezone: e.target.value })}
                className="w-56"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
